import { PasswordProps } from 'antd/lib/input/Password';
import { TTextTranslationNamespaces } from 'types';
import { Icon, IconName } from 'components/Icon/Icon';
import Text from 'components/Text/Text';
import { NamePath } from 'rc-field-form/lib/interface';
import React from 'react';
import { Form, Input, InputNumber } from 'antd';
import { FormItemProps, Rule } from 'antd/lib/form';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import style from './FormInputNew.module.scss';

type TDefaultInputProps = Omit<PasswordProps, 'onChange'>;

interface IFormInput extends TDefaultInputProps {
	name: string;
	label: string;
	onChange?: (value: string) => void;
	onChangeNumber?: (value: number | null) => void;
	getValueFromEvent?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	placeholder?: string;
	className?: string;
	containerClassName?: string;
	labelClassName?: string;
	validationRules?: Rule[];
	validateStatus?: FormItemProps['validateStatus'];
	help?: FormItemProps['help'];
	translationNameSpace?: TTextTranslationNamespaces;
	testId?: string;
	type?: 'text' | 'password' | 'textArea' | 'number';
	dependencies?: NamePath[];
	min?: number;
	max?: number;
	precision?: number;
}

export const FormInputNew: React.FC<IFormInput> = React.memo(
	({
		name,
		label,
		placeholder = 'inputPlaceholder',
		onChange,
		onChangeNumber,
		getValueFromEvent,
		validationRules,
		validateStatus,
		help,
		translationNameSpace = 'form',
		testId,
		className,
		containerClassName,
		labelClassName,
		type = 'text',
		dependencies,
		min,
		max,
		precision,
		disabled = false,
		...restProps
	}) => {
		const { t } = useTranslation(translationNameSpace);

		const containerClasses = classNames(style.container, containerClassName);
		const inputClasses = classNames(style.input, className);
		const textAreaClasses = classNames(style.textArea, className);
		const inputNumberClasses = classNames(style.inputNumber, className);

		const isRequired = validationRules?.some((rule) => {
			if (typeof rule === 'object') {
				return rule?.required;
			}
			return false;
		});

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			onChange?.(e.target.value);
		};

		const handleChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
			onChange?.(e.target.value);
		};

		const handleChangeInputNumber = (e: Nullable<number>) => {
			onChangeNumber?.(e);
		};

		const renderInputElement = () => {
			switch (type) {
				case 'text':
					return (
						<Input
							className={inputClasses}
							onChange={!getValueFromEvent ? handleChange : undefined}
							allowClear={true}
							placeholder={t(placeholder)}
							disabled={disabled}
							data-test={`${testId}Input`}
							{...restProps}
						/>
					);
				case 'password':
					return (
						<Input.Password
							className={inputClasses}
							onChange={!getValueFromEvent ? handleChange : undefined}
							placeholder={t(placeholder)}
							disabled={disabled}
							iconRender={(visible) => <Icon name={visible ? IconName.eye : IconName.eyeOff} size="lg" />}
							{...restProps}
						/>
					);
				case 'textArea':
					return (
						<Input.TextArea
							className={textAreaClasses}
							onChange={!getValueFromEvent ? handleChangeTextArea : undefined}
							allowClear={true}
							autoSize={true}
							placeholder={t(placeholder)}
							disabled={disabled}
						/>
					);
				case 'number':
					return (
						<InputNumber
							className={inputNumberClasses}
							onChange={!getValueFromEvent ? handleChangeInputNumber : undefined}
							placeholder={t(placeholder)}
							min={min}
							max={max}
							disabled={disabled}
							precision={precision}
						/>
					);
				default:
					return (
						<Input
							className={inputClasses}
							onChange={!getValueFromEvent ? handleChange : undefined}
							allowClear={true}
							placeholder={t(placeholder)}
							disabled={disabled}
							data-test={`${testId}Input`}
							{...restProps}
						/>
					);
			}
		};

		return (
			<div data-test={testId} className={containerClasses}>
				<div className={style.labelRow}>
					<Text
						className={labelClassName}
						translationNameSpace={translationNameSpace}
						translationKey={label}
						size="xs"
						theme="neutralDark"
						display="inline"
					/>
					{!!isRequired && <Text translationKey="*" size="xs" theme="neutralLight" display="inline" />}
				</div>
				<Form.Item
					name={name}
					rules={validationRules}
					getValueFromEvent={getValueFromEvent}
					dependencies={dependencies}
					validateStatus={validateStatus}
					help={help}>
					{renderInputElement()}
				</Form.Item>
			</div>
		);
	},
);
